import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FiChevronDown, FiChevronRight, FiExternalLink } from "react-icons/fi";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../../components/BaseUI/Modal";
import Button from "../../../../components/BaseUI/Button";
import { formatDateTimeTrInAppTz } from "../../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

function formatProdTs(iso) {
  const raw = formatDateTimeTrInAppTz(iso);
  return raw.replace(/[,\s]+(?=\d{2}:\d{2}:\d{2})/, " · ");
}

const ProductionDetailsModal = ({
  isOpen,
  onClose,
  machine,
  from,
  to,
  activePartId,
  getToken,
  onOpenReport,
  canOpenReport = false,
}) => {
  const [groups, setGroups] = useState([]);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());

  const machineId = machine?._id;
  const activePartKey = String(activePartId || "").toLowerCase();

  const fetchRecords = useCallback(async () => {
    if (!machineId || !from || !to) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_URL}/api/machine-live/${machineId}/production-records`,
        {
          params: { from, to },
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );
      const nextGroups = res.data?.data?.groups || [];
      setGroups(nextGroups);
      setTotalQuantity(Number(res.data?.data?.totalQuantity) || 0);
      setExpanded(new Set());
    } catch (err) {
      setError(
        err?.response?.data?.message || "Üretim kayıtları yüklenemedi.",
      );
      setGroups([]);
      setTotalQuantity(0);
    } finally {
      setLoading(false);
    }
  }, [machineId, from, to, getToken]);

  useEffect(() => {
    if (!isOpen) return;
    fetchRecords();
  }, [isOpen, fetchRecords]);

  const hasParts = groups.some(
    (g) => g.partId || g.name || g.code || (g.quantity ?? 0) > 0,
  );

  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const machineName = machine?.name || "—";
  const machineCode = machine?.code || "";

  const rows = useMemo(() => groups, [groups]);

  return (
    <Modal
      isOpen={isOpen}
      width="medium"
      scrollBehavior="inside"
      onClose={onClose}
      className="mlm-details-modal"
    >
      <ModalHeader>
        <div className="mlm-details-modal__header">
          <span>Vardiya Üretim Detayı</span>
          <button
            type="button"
            className="mlm-card-detail-btn"
            title="Üretim raporuna git"
            aria-label="Üretim raporuna git"
            disabled={!canOpenReport}
            onClick={onOpenReport}
          >
            <FiExternalLink size={13} />
            <span>Rapor</span>
          </button>
        </div>
      </ModalHeader>
      <ModalBody showCloseButton={false}>
        <div className="mlm-details-section">
          {loading ? (
            <div className="mlm-details-empty">Yükleniyor…</div>
          ) : error ? (
            <div className="mlm-details-empty">{error}</div>
          ) : hasParts ? (
            <div className="mlm-prod-tree-wrap">
              <table className="mlm-prod-tree">
                <thead>
                  <tr>
                    <th>Parça</th>
                    <th>Zaman / Operatör</th>
                    <th className="mlm-prod-tree__num">Üretim</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((group, gIdx) => {
                    const groupKey = group.partId || `part-${gIdx}`;
                    const isOpenGroup = expanded.has(groupKey);
                    const isActive =
                      group.partId &&
                      String(group.partId).toLowerCase() === activePartKey;
                    const recordCount = group.records?.length || 0;
                    const Chevron = isOpenGroup ? FiChevronDown : FiChevronRight;

                    return (
                      <React.Fragment key={groupKey}>
                        <tr
                          className={`mlm-prod-tree__group${isActive ? " is-active" : ""}`}
                          onClick={() => toggleGroup(groupKey)}
                        >
                          <td>
                            <button
                              type="button"
                              className="mlm-prod-tree__toggle"
                              aria-expanded={isOpenGroup}
                            >
                              <Chevron size={14} />
                              <span className="mlm-prod-tree__group-name">
                                {group.name || "—"}
                              </span>
                              {group.code ? (
                                <span className="mlm-prod-tree__badge">
                                  {group.code}
                                </span>
                              ) : null}
                            </button>
                          </td>
                          <td>
                            <span className="mlm-prod-tree__meta">
                              {recordCount} kayıt
                            </span>
                          </td>
                          <td className="mlm-prod-tree__num">
                            {group.quantity ?? 0}
                          </td>
                        </tr>
                        {isOpenGroup &&
                          (group.records || []).map((rec) => {
                            const partLabel = [rec.partCode, rec.partName]
                              .filter(Boolean)
                              .join("/") || group.name || "—";
                            const who = rec.operatorName || "—";
                            const whoLabel = rec.operatorAuto
                              ? `${who}*`
                              : who;
                            return (
                              <tr
                                key={rec._id}
                                className="mlm-prod-tree__child"
                              >
                                <td>
                                  <div className="mlm-prod-tree__child-part">
                                    <span className="mlm-prod-tree__rail" />
                                    <span title={partLabel}>{partLabel}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="mlm-prod-tree__detail">
                                    <span className="mlm-prod-tree__sub">
                                      {formatProdTs(rec.at)}
                                      {" · "}
                                      <strong className="mlm-prod-tree__who">
                                        {whoLabel}
                                      </strong>
                                    </span>
                                  </div>
                                </td>
                                <td className="mlm-prod-tree__num">
                                  {rec.quantity ?? 0}
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="mlm-prod-tree__total">
                    <td>TOPLAM</td>
                    <td />
                    <td className="mlm-prod-tree__num">{totalQuantity}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="mlm-details-empty">
              Bu vardiyada kayıtlı üretim bulunmuyor.
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button appearance="primary-subtle" type="button" onClick={onClose}>
          Kapat
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ProductionDetailsModal;
